import React from 'react';
import { Button, Col, Form as BsForm } from '../../ui-kit';
import { connect, Field as FormikField, FastField } from 'formik';
import { castArray, get } from 'lodash';
// TODO: replace with new DateTimePicker
import DateTimePicker from '../DateTimePicker';

export const Form = connect(({ formik: { handleReset, handleSubmit }, ...props }) => {
  return <BsForm onSubmit={handleSubmit} onReset={handleReset} {...props} />;
});

export const HorizontalForm = props => {
  return <Form {...props} horizontal />;
};

// Don't use this directly, use one of the helpers like "EmailField",
// "TextField" etc.
const Field = props => {
  // Some fields, especially heavily custom ones do not play well
  // with Formik in terms of performance. For these types of fields
  // we should use FastField.
  // Note that FastField cannot be used in all scenarios, as they
  // can lead to unexpected scenarios or values not updating.
  const FkField = props.fast ? FastField : FormikField;
  return <FkField {...props} component={FormikCustomField} />;
};

export const EmailField = props => {
  const { type, ...propsWithoutType } = props;
  return <Field {...propsWithoutType} type="email" />;
};

export const PasswordField = props => {
  const { type, ...propsWithoutType } = props;
  return <Field {...propsWithoutType} type="password" />;
};

export const TextField = props => {
  const { type, ...propsWithoutType } = props;
  return <Field {...propsWithoutType} type="text" />;
};

export const NumberField = props => {
  const { type, ...propsWithoutType } = props;
  return <Field {...propsWithoutType} type="number" />;
};

export const DateTimeInput = props => {
  const { type, ...propsWithoutType } = props;
  return <Field {...propsWithoutType} type="datetime" fast={true} />;
};

export const Select = props => {
  const { type, ...propsWithoutType } = props;
  return <Field {...propsWithoutType} type="select" />;
};

export const Checkbox = props => {
  const { type, ...propsWithoutType } = props;
  return <Field {...propsWithoutType} type="checkbox" />;
};

const FormikCustomField = ({
  field, // { name, value, onChange, onBlur }
  form: { touched, errors, status, setFieldValue, setFieldTouched }, // also values, setXXXX, handleXXXX, dirty, isValid, status, etc.
  type,
  label_i18n,
  inputOnly, // if true, only render the actual form element, no label or Cols
  ...props
}) => {
  const label = props.label || (label_i18n && I18n.t(label_i18n));
  const warning = null; // not supported (yet) by Formik
  const statusMessage = status && get(status, field.name); // backend error
  const error = [statusMessage, get(errors, field.name)].filter(Boolean).join('. ');
  const isTouched = get(touched, field.name);
  const validationState = (isTouched && error && 'error') || (warning && 'warning') || null;

  const stateValidity = { error: 'invalid', warning: 'valid', success: 'valid' };
  const validationType = stateValidity[validationState];

  // const displayMessage = isTouched && (error || warning);
  const messageContent = castArray([error || warning]).join('. ');
  // const message = displayMessage && <Form.Text>{messageContent}</Form.Text>;

  let feedback = <BsForm.ControlFeedback type={validationType}>{messageContent}</BsForm.ControlFeedback>;
  let component = <BsForm.Control isInvalid={validationState} {...props} {...field} type={type} placeholder={label} />;

  if (type === 'datetime') {
    // have to manually set field value because DateTimePicker doesn't adhere to
    // standards. It returns a date, instead of an event.
    const onDateChange = date => {
      setFieldValue(field.name, date);
      setFieldTouched(field.name, true); // onBlur is broken, must touch manually
    };
    const onDateBlur = () => {}; // onBlur is broken
    const { onChange, onBlur, ...fieldProps } = field;

    component = <DateTimePicker onChange={onDateChange} onBlur={onDateBlur} {...props} {...fieldProps} />;
    feedback = null; // feedback icon doesn't work for DateTimePicker
  } else if (type === 'checkbox') {
    component = <BsForm.Check {...props} {...field} />;
  } else if (type === 'select') {
    component = (
      <BsForm.Select {...props} {...field} placeholder={label}>
        {Object.entries(props.options).map(([value, text]) => (
          <option key={value} value={value}>
            {text}
          </option>
        ))}
      </BsForm.Select>
    );
  }

  // function for conditionally wrapping a component in a bootstrap Col
  const colWrap = (span, shouldWrap, component) => (shouldWrap ? <Col sm={span}>{component}</Col> : component);

  const labelComponent = (
    <BsForm.Label column sm={3}>
      {label}
    </BsForm.Label>
  );

  const inputGroup = (
    <React.Fragment>
      {component}
      {feedback}
    </React.Fragment>
  );

  const formGroupContent = (
    <React.Fragment>
      {!inputOnly && labelComponent}
      {colWrap(7, !inputOnly, inputGroup)}
    </React.Fragment>
  );

  return <BsForm.Group>{formGroupContent}</BsForm.Group>;
};

export const FormButtons = props => (
  <BsForm.Group className="mb-3">
    <Col sm={7} smOffset={3}>
      {props.children}
    </Col>
  </BsForm.Group>
);

export const SubmitButton = connect(
  ({ formik: { isSubmitting }, text = I18n.t('save'), className, onClick, variant = 'success' }) => {
    text = isSubmitting ? I18n.t('saving') : text;
    return (
      <Button type="submit" variant={variant} disabled={isSubmitting} className={className} onClick={onClick}>
        {text}
      </Button>
    );
  }
);

export const ResetButton = connect(({ formik: { isSubmitting, handleReset } }) => {
  const text = I18n.t('reset');
  return (
    <Button type="button" onClick={handleReset} disabled={isSubmitting}>
      {text}
    </Button>
  );
});
